"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { mergeClasses } from "@trex-2b/libs";
import { GROWTH_ROUTES } from "@trex-2b/libs/config/routes";
import { useLandingLoginGuard } from "@trex-2b/ui/auth/use-landing-login-guard";

import {
  ContactSalesAnimatedStepCards,
  type ContactSalesCardStep,
} from "@/app/landing-page/_components/contact-sales-animated-step-cards";
import { StaggerFadeInUp } from "@/lib/motion/stagger-fade-in-up";

/**
 * Figma: StandardDesign · node 8904:4750 — ContactSalesLayout (hero + form row).
 * Form card 8904:4760; after submit → thanks card 8904:4846.
 */
function StarStrokeDecoration({ className }: { className?: string }) {
  return (
    <div
      className={mergeClasses("relative h-[363px] w-[343px]", className)}
      aria-hidden
    >
      <div className="absolute inset-[-19.83%_0_0_0]">
        <Image
          alt=""
          src={"/images/landing/contact-sale-star.svg"}
          width={343}
          height={363}
          className="block size-full max-w-none object-contain"
        />
      </div>
    </div>
  );
}

export function ContactSalesLayout({ className }: { className?: string }) {
  const [cardStep, setCardStep] = useState<ContactSalesCardStep>("form");
  const router = useRouter();
  const { runWithLogin } = useLandingLoginGuard();

  return (
    <div
      className={mergeClasses(
        "font-inter",
        "relative flex min-w-0 flex-1 flex-col items-center gap-2.5 py-[150px] justify-center",
        className,
      )}
    >
      <div
        className="pointer-events-none absolute left-0 top-0 h-[828px] w-full overflow-hidden bg-white"
        aria-hidden
      >
        <StarStrokeDecoration className="absolute inset-[0_77.31%_56.16%_0]" />
      </div>
      <div className="relative z-1 flex w-full min-w-0 justify-center">
        <StaggerFadeInUp
          className="flex w-fit max-w-full shrink-0 items-center gap-[98px]"
          staggerChildren={90}
          delay={0}
        >
          {[
            <div
              key="contact-sales-copy"
              className="flex w-[693px] shrink-0 flex-col gap-6 leading-none text-v2-content-primary not-italic"
            >
              <h1
                className={mergeClasses(
                  "font-ancizar",
                  "w-full capitalize text-[72px] leading-[86px] tracking-[-3.6px]",
                )}
              >
                Scale your ecosystem with precision.
              </h1>
              <p className="w-full text-[18px] font-normal leading-6 tracking-[-0.3px]">
                {`Unlock high-intent users and bespoke growth strategies. Talk to our experts to build a tailored plan for your project's success.`}
              </p>
            </div>,
            <ContactSalesAnimatedStepCards
              key="contact-sales-cards"
              step={cardStep}
              onSubmitted={() => setCardStep("thanks")}
              onTryDemo={() => {
                runWithLogin(() => router.push(GROWTH_ROUTES.ON_BOARDING));
              }}
            />,
          ]}
        </StaggerFadeInUp>
      </div>
    </div>
  );
}
